import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';
import {
  AuthCallback,
  appConfig,
  features,
  createAuthGuard,
  createPaymentFlowGuard,
  createTitleHandler,
  getFeatureFlagOverridesFromAppConfig,
  loginRoute,
  logoutRoute,
  signupRoute,
} from '@peanuds/app-shell';
import AppLayout from '../components/AppLayout.vue';
import AuthLayout from '../components/AuthLayout.vue';

// Customer pages
import DashboardPage from '../pages/DashboardPage.vue';

// Feature routes
import { authRoutes } from '@peanuds/app-shell';
import { moneyRoutes } from '@peanuds/money';
import { batchCollectionsRoutes } from '@peanuds/batch-collections';
import { cardsRoutes } from '@peanuds/cards';
import { analyticsRoutes } from '@peanuds/analytics';
import { dataExportRoutes } from '@peanuds/data-export';
import { settingsRoutes } from '@peanuds/settings';
import { accountsRoutes } from '@peanuds/accounts';
import { paymentsApprovalRoutes } from '@peanuds/payments-approval';
import { featureConfig } from '../feature-config';

const featureFlagOverrides = getFeatureFlagOverridesFromAppConfig(appConfig);
const defaultPaymentsApprovalRouteEnabled =
  featureConfig.flags?.[features.paymentsApproval] ?? false;
const isPaymentsApprovalRouteEnabled =
  featureFlagOverrides[features.paymentsApproval] ?? defaultPaymentsApprovalRouteEnabled;
const bebawaPaymentsApprovalRoutes = isPaymentsApprovalRouteEnabled ? paymentsApprovalRoutes : [];

const defaultDataExportRouteEnabled = featureConfig.flags?.[features.dataExport] ?? false;
const isDataExportRouteEnabled =
  featureFlagOverrides[features.dataExport] ?? defaultDataExportRouteEnabled;
const bebawaDataExportRoutes = isDataExportRouteEnabled ? dataExportRoutes : [];

const routes = [
  loginRoute,
  signupRoute,
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      // Customer-specific pages
      {
        path: 'auth/callback',
        name: 'auth-callback',
        component: AuthCallback,
        meta: { requiresAuth: false },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: DashboardPage,
        meta: { requiresAuth: true },
      },
      // Shared feature routes
      ...moneyRoutes,
      ...cardsRoutes,
      ...batchCollectionsRoutes,
      ...accountsRoutes,
      ...analyticsRoutes,
      ...settingsRoutes,
      ...bebawaPaymentsApprovalRoutes,
      ...bebawaDataExportRoutes,
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    meta: { requiresAuth: false },
    children: [...authRoutes],
  },
  // Logout route at root level (Auth0 redirects to /logout after logout)
  logoutRoute,
  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../pages/NotFoundPage.vue'),
    meta: { requiresAuth: false },
  },
] as const satisfies readonly RouteRecordRaw[];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition ? savedPosition : { left: 0, top: 0 };
  },
});

// META TITLE HANDLER (from app-shell utilities)
router.afterEach(createTitleHandler('Bebawa'));

// GUARDS (from app-shell utilities)
router.beforeEach((to) => {
  if (to.path.replace(/\/+$/, '') === '/auth/login') {
    reportAuthFlowBlocked({
      operation: 'canonicalize-auth-login-route',
      failureKind: 'legacy-auth-login-route',
      tags: {
        hasRedirectQuery: typeof to.query.redirect === 'string' || Array.isArray(to.query.redirect),
        hasHash: to.hash.length > 0,
      },
    });

    return { name: 'login', query: to.query, hash: to.hash };
  }

  return true;
});
router.beforeEach(createAuthGuard());
router.beforeEach((to) => {
  if (to.path === '/') {
    return '/dashboard';
  }

  return true;
});
router.beforeEach(createPaymentFlowGuard());

export default router;
